import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { createPaymentSession } from "./api";

// Every payment method the session declares is offered: the aggregate "flow"
// component renders the whole list Checkout returns for the session, with its
// own method picker, so what the customer sees is controlled entirely by the
// backend/Checkout dashboard and nothing is filtered client-side.
const FLOW_CONTAINER_ID = "flow-container";

// The SDK is loaded by the deferred <script id="checkout-script"> in
// public/index.html, so it may not have executed by the time this hook runs.
const loadFlowSdk = () =>
  new Promise((resolve, reject) => {
    if (window.CheckoutWebComponents) {
      resolve(window.CheckoutWebComponents);
      return;
    }
    const script = document.getElementById("checkout-script");
    if (!script) {
      reject(new Error("Checkout Flow SDK script tag missing from index.html"));
      return;
    }
    script.addEventListener(
      "load",
      () =>
        window.CheckoutWebComponents
          ? resolve(window.CheckoutWebComponents)
          : reject(new Error("Checkout Flow SDK loaded without a global")),
      { once: true }
    );
    script.addEventListener(
      "error",
      () => reject(new Error("Checkout Flow SDK failed to load")),
      { once: true }
    );
  });

// Laravel reports "staging"/"production"; Flow only accepts "sandbox"/"production".
const isProductionEnvironment = (environment) =>
  environment === "production" || environment === "live";

// Flow wants a BCP-47 tag; the route only carries "en" / "ar".
const toFlowLocale = (langSlug) => (langSlug === "ar" ? "ar" : "en-GB");

const useFormPayment = ({ id, enabled = true, isRenewal = false }) => {
  const [errorMsgCard, setErrorMsgCard] = useState("");
  const [flowReady, setFlowReady] = useState(false);
  const [flowMounted, setFlowMounted] = useState(false);
  const [mountFailed, setMountFailed] = useState(false);
  const [cardClick, setCardClick] = useState(true);

  const componentRef = useRef(null);

  const { t } = useTranslation(["common"]);

  const { lang: langSlug } = useParams();
  const navigate = useNavigate();
  let langString = langSlug;

  useEffect(() => {
    if (!langSlug) {
      const lang = localStorage.getItem("language_type");
      if (lang === "ar") {
        navigate("/ar");
      } else if (lang === "en") {
        navigate("/en");
      } else {
        navigate("/en");
      }
    }
  }, []);

  const showError = useCallback(
    ({ persist = false } = {}) => {
      setErrorMsgCard(t("Something_wrong"));
      if (!persist) setTimeout(() => setErrorMsgCard(""), 4000);
    },
    [t]
  );

  // Wait for the real prop. useSubscription writes localStorage "id" from the
  // same response that sets this prop, so before that write localStorage still
  // holds the *previous* load's order — using it as a fallback opens a payment
  // session against the wrong order. String() keeps the effect key stable when
  // the prop arrives as a number.
  const orderId = id ? String(id) : "";

  useEffect(() => {
    if (!enabled || !orderId) return;

    let cancelled = false;

    // A re-run remounts from scratch, so the gating state has to go back to its
    // pre-mount values. Leaving flowReady true would let the component below
    // drop the skeleton before the container is populated.
    setFlowReady(false);
    setFlowMounted(false);
    setMountFailed(false);

    const mountFlow = async () => {
      try {
        const CheckoutWebComponents = await loadFlowSdk();

        // Laravel creates the session: it owns amount, currency, and the
        // success/failure URLs Flow redirects to, so it needs the locale too.
        const response = await createPaymentSession({
          order_id: orderId,
          lang: langString === "ar" ? "ar" : "en",
        });

        if (cancelled) return;

        const payload = response?.data;
        const paymentSession = payload?.payment_session;

        if (
          response?.success !== true ||
          !paymentSession?.id ||
          !paymentSession?.payment_session_secret
        ) {
          throw new Error(
            `create-payment-session returned no usable session: ${response?.message}`
          );
        }

        // Flow renders its own pay button, so the old
        // `paymentOptionStatus === 2 ? RENEW : PAY` switch is expressed through
        // its translations. pay_button.pay_with is the method-specific label
        // ("Pay with Card"), pay_button.pay the plain one. Only the renewal
        // wording is overridden; Flow's own localised "Pay with X" stands.
        const locale = toFlowLocale(langString);
        const renewLabel = t("RENEW").trim();
        const translations = isRenewal
          ? {
              [locale]: {
                "pay_button.pay": renewLabel,
                "pay_button.pay_with": renewLabel,
              },
            }
          : undefined;

        // QPay is the debit-card route; the card method is credit-only.
        // Compared case-insensitively since the SDK's casing of card_type is
        // not guaranteed. An unknown card_type (BIN not in Checkout's table)
        // is let through.
        const rejectDebitCards = (_self, cardMetadata) =>
          String(cardMetadata?.card_type).toLowerCase() === "debit"
            ? {
                continue: false,
                errorMessage: t(
                  "debitNotAccepted",
                  "Debit cards are not accepted. Please pay with QPay."
                ),
              }
            : { continue: true };

        // Key and environment come from the session response, not from .env:
        // they must match the secret key that created the session, and
        // REACT_APP_PRODUCTION can say "true" while the session is a sandbox one.
        const checkout = await CheckoutWebComponents({
          publicKey: payload.public_key,
          environment: isProductionEnvironment(payload.environment)
            ? "production"
            : "sandbox",
          locale,
          paymentSession,
          translations,
          onReady: () => {
            if (!cancelled) setFlowReady(true);
          },
          onPaymentCompleted: () => {
            // Flow also redirects to the session's success_url; this covers the
            // non-redirect path. Fulfilment happens on the webhook, not here.
            navigate(`/order-summary/${orderId}/success/${langString}`);
          },
          onError: () => showError(),
        });

        if (cancelled) return;

        // The BIN check is component-level, so it goes on create() rather than
        // the config above. isAvailable() is mandatory: the SDK throws
        // component_is_available_not_checked if mount() is called without it.
        const flow = checkout.create("flow", {
          onCardBinChanged: rejectDebitCards,
        });

        if (!(await flow.isAvailable())) {
          throw new Error("No enabled payment method is available to mount");
        }
        if (cancelled) return;

        await flow.mount(`#${FLOW_CONTAINER_ID}`);
        componentRef.current = flow;

        if (cancelled) return;
        setFlowMounted(true);
      } catch (error) {
        if (cancelled) return;
        console.log(
          "Flow mount error: ",
          error?.response?.status
            ? `${error.response.status} from ${error.response.config?.url}`
            : error
        );
        setMountFailed(true);
        showError({ persist: true });
      }
    };

    mountFlow();

    return () => {
      cancelled = true;
      const component = componentRef.current;
      componentRef.current = null;
      if (!component) return;
      try {
        component.unmount();
      } catch (error) {
        console.log("Flow unmount error: ", error);
      }
    };
  }, [enabled, orderId, langString, isRenewal]);

  // `flowReady` alone must never gate the DOM: for a component that is not
  // async-loaded Flow fires onReady synchronously inside its own mount(), i.e.
  // before mount() has resolved and the iframes are in place. Dropping the
  // skeleton on flowReady alone therefore uncovers a container that is still
  // empty, so mount completion is required too.
  const ready = flowReady && flowMounted;

  return {
    containerId: FLOW_CONTAINER_ID,
    ready,
    mountFailed,
    errorMsgCard,
    cardClick,
    setCardClick,
    t,
  };
};
export default useFormPayment;
